import React, { MouseEvent, ChangeEvent, useRef, useEffect, useState } from 'react';

import { injectIntl, IntlShape } from 'react-intl';

// axios
import axios from 'axios';
import { apiClient } from 'api-client';


// alerts
import { useSnackbar } from "notistack";

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setItem, setItemForm } from 'redux/actions/inventoryActions';
import { setRefresh } from 'redux/actions/pageActions';
import { extractDataItem } from 'common/extractData';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import LoadingComponent from 'components/LoadingComponent';

import Popper from '@mui/material/Popper';

// icon
import CategoryIcon from '@mui/icons-material/Category';

// enumns
import { isActiveItemEnum, enumItemIsActive } from 'common/enums/DevicesStateEnum';

// sanitize
import DOMPurify from "dompurify";

// types
import { ItemEditData } from 'types/typesRedux';

type Props = {
  intl: IntlShape,
  itemId: number,
  onEditItem: (bool: boolean) => void,
  onCloseWindow: (bool: boolean) => void,
}

const EditItem = ( {intl, itemId, onEditItem, onCloseWindow}: Props) => {
  
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const [localLoading, setLocalLoading] = useState<boolean>(true);

  const item = useSelector( (state: ItemEditData) => state.item.item );
  const itemForm = useSelector( (state: ItemEditData) => state.item.itemForm );

  const variant = ( item && item.categoryVariant && item.categoryVariant !== undefined ) ? item.categoryVariant.map( (item: string) =>  item ) : null;

  // set data from inputs to redux
  const handleIsActive = ( e: ChangeEvent<{}>, newValue: string ) => {
    let isActive = newValue ? newValue : item.isActive;
    let modifiedStatus = isActive === `${intl.formatMessage({id:'tableInventory.statusTrue'})}` ? true : false;
    dispatch(setItemForm( { ...itemForm, 'isActive': modifiedStatus } ));
  }
 
  const handleVariant = ( e: ChangeEvent<{}>, newValue: string ) => {
    dispatch(setItemForm( { ...itemForm, 'variant': newValue } ));
  }
 
  const handleNotes = ( e: ChangeEvent<HTMLInputElement> ) => {
    dispatch(setItemForm( { ...itemForm, 'notes': e.target.value } ));
  }

  const handleInputDescription = ( e: ChangeEvent<HTMLInputElement>, newValue: string, type?: 'kayak' | 'note' ) => {

    newValue = DOMPurify.sanitize( newValue );

    if( type === 'note' ) {
      dispatch(setItemForm({
        ...itemForm,
        notes: newValue,
      })
      )
    } else if ( type === 'kayak' ) {
      dispatch(setItemForm({
        ...itemForm,
        inventoryId: newValue,
      })
      )
    }
  };

  // get item
  useEffect(() => {
    dispatch(setItemForm( {
      inventoryId: null,
      isActive: null,
      notes: '',
      variant: '',
      } 
    ));
    getItem( itemId );
  }, [itemId]);


  const closeWindow = () => {
    onCloseWindow( false );
    dispatch(setItemForm( {
      inventoryId: null,
      isActive: null,
      notes: '',
      variant: '',
    } 
  ));
  } 

  // If submit, edit item
  const handleSubmit = async (e: MouseEvent<HTMLButtonElement> ) => {
    e.preventDefault();
    if ( ( itemForm.isActive !== null ) || itemForm.variant || itemForm.notes || itemForm.inventoryId ) {
      editItem( itemId ); 
      // send parent close modal
      onEditItem( true );         
    } else {
      enqueueSnackbar(`${intl.formatMessage({id:'inventoryAlert.edit.empty'})}`, { variant: 'error' });
    }
  };

  // Handle get item for edit modal/////////////////////////////
  const getItem = async ( id: number ) => {
    const cancelToken = axios.CancelToken.source()

    try {
      const response = await apiClient.get(`/v1/items/${id}`,{
        cancelToken:cancelToken.token
      });

      if (response) {
        let responseData = extractDataItem(intl)( response.data );
        dispatch(setItem( responseData ));
        setLocalLoading( false );
        // console.log( response.data )
      }
    } catch ( err ){
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        // todo:handle error
      }
    }
    return () => cancelToken.cancel()
  } 

  // Handle edit item /////////////////////////////
  const editItem = async ( id: number ) => {
    const cancelToken = axios.CancelToken.source();

    try {
      await apiClient.patch(`/v1/items/${id}`,{
        inventoryId: itemForm.inventoryId ? itemForm.inventoryId : item.inventoryId,
        isActive: itemForm.isActive === false || itemForm.isActive === true ? itemForm.isActive : enumItemIsActive(intl)(item.isActive),
        notes: itemForm.notes ? itemForm.notes : item.notes,
        variant: itemForm.variant ? itemForm.variant : item.variant,
        categoryId: item.categoryId,

        cancelToken:cancelToken.token
        }
      ).then( ( response ) => {
        if ( response ) {
          enqueueSnackbar(`${intl.formatMessage({id:'inventoryAlert.edit.true'})}`, { variant: 'success' });
          setTimeout(() => {
            dispatch(setRefresh( { refreshInventory: true } ));
          }, 200)
          dispatch(setItemForm( {
            inventoryId: null,
            isActive: null,
            notes: '',
            variant: '',
          } 
        ));
        }
    });
    } catch ( err ){
      if (axios.isCancel(err)) {
        console.log("canceled");
      } else {
        enqueueSnackbar(`${intl.formatMessage({id:'inventoryAlert.edit.false'})}`, { variant: 'error' });
      }
    };
    return () => cancelToken.cancel();
  }
  


  if ( localLoading === true ) {
    return <LoadingComponent />
  }

  return (
    <Grid container sx={{ position: 'fixed', top: '0%', left: '0%',
      backgroundColor:"rgba( 0, 0, 0, 0.5 ) !important", 
      width:"100vw", height:"100vh", zIndex:"1300" }}
    >

      <Card
        sx={{ 
          minWidth: "24rem", width: "auto", maxWidth: "27rem",
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          // backgroundColor: "#5ada55",
        }}
      >        

        <MDBox
          display="flex"
          flexDirection="column"
          p={2}
          m={0}
          sx={{ listStyle: "none" }}
        >    

          <MDBox sx= {{ display: "flex",
            flexDirection: "row", gap: "1rem" }} 
          >
            <MDBox className= "badge"
              sx={{ 
                // backgroundColor: "#17C063",
                p: 1, mt: -4, ml: 0,
                borderRadius: ({ borders: { borderRadius } }) => borderRadius.xl, 
              }}
              display="grid"
              justifyContent="center"
              alignItems="center"
              bgColor="#17C063"
              color="white"
              width="4.5rem"
              height="4.5rem"
              shadow="md"
              borderRadius="lg"
              variant="gradient"  
              fontSize= "2.2rem"        
            >
              <Icon sx={{marginBottom:"0.8rem"}} ><CategoryIcon /></Icon>
            </MDBox>

            <MDBox
              sx={{
                diplay: "flex", flexDirection: "column",
              }}
            >
              <MDTypography
                sx={{
                  fontSize: "1.2rem", fontWeight: "700",
                }}
              >
                {intl.formatMessage({id:'inventory.edit.title'})}
              </MDTypography>
              <MDTypography
                sx={{
                  fontSize: "0.75rem", fontWeight: "400",
                  color: "#7B809A",
                }}
              >
                {item.beaconMac ?? ""}
              </MDTypography>
            </MDBox>
                            
          </MDBox>

          <MDBox
            sx={{
              display: "flex", flexDirection: "row",
              justifyContent: "space-between", alignItems: "center",
              paddingBottom: "0.8rem", paddingTop: "0.8rem",
            }}
          >
            <MDTypography
              sx={{
                fontSize: "0.85rem", fontWeight: "700",
                width: "45%", paddingLeft: "1rem"
              }}
            >
              {item.name ?? ""}
            </MDTypography>

            { item.name === intl.formatMessage({ id: 'tableInventory.kayak' }) ? (
              <MDInput
                // className= "noteItemInput"
                type="text"
                name="description"
                placeholder={item?.inventoryId ? item?.inventoryId : intl.formatMessage({id:'inventory.edit.idBoat'})}
                // disabled={isDisabled}
                value={ itemForm.inventoryId }
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputDescription(e, e.target.value, 'kayak')}
                  inputProps={{
                    maxLength: 20,
                  }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D2D6DA',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#7B809A',
                    opacity: 1,
                  },
                }}
                style={{ width: "45%", }}
              />
            ) : item.name === intl.formatMessage({ id: 'tableInventory.vest' }) ? (
              <Autocomplete
                options={ variant }
                onChange={ handleVariant }
                disableClearable
                sx={{ marginTop: "-0.5rem", width: "45%", }}
                ListboxProps= {{
                  sx: {
                    "& div": {  minWidth: "25rem !important", width: "100%"  },
                    "& li": {  marginLeft: "-0.75rem", marginRight: "0rem", fontSize: "0.85rem !important", borderRadius: "none", },
                    // "& li:last-child": { color: "#000", backgroundColor: "#FF7866", borderRadius: "0px", fontWeight: "400"},
                  }
                }}
                renderInput={(params: any) => (
                  <MDInput
                    className="autocomplete"
                    {...params}
                    right={'16px'}
                    left={'-5px'}
                    sx={{
                      width: "100%",
                      '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
                        marginRight: 0, 
                        right: "5px",
                      '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment:hover': {
                        cursor: "pointer",
                        /* Define other hover styles as needed */
                      },
                      },
                      '& button': {
                        paddingRight: "0rem",
                        paddingLeft: "0rem",
                      },
                    }}
                    inputProps={{
                      ...params.inputProps,
                      style: {
                        paddingRight: '0', 
                        paddingLeft: '0.3rem', 
                        fontSize: '0.75rem', 
                      },
                      readOnly: true,
                    }}
                    size="small"
                    label={itemForm?.variant ? intl.formatMessage({ id: 'inventory.edit.variant' }) : (item?.variant || intl.formatMessage({ id: 'inventory.edit.variant' }))}
                    variant="outlined"
                  />
                )}
              />
            ) : null
            }           

          </MDBox>

          <MDBox sx={{ position: "relative", width: "100%" }}>
            <MDBox
              sx={{
                display: "flex", flexDirection: "column",
              }}
            >

              { item.notes && (
                <MDTypography
                  sx={{
                    fontSize: "0.625rem", fontWeight: "400",
                    color: "#807E7B"
                  }}
                >
                  {intl.formatMessage({id:'inventory.edit.notes'})}
                </MDTypography>
              )}
              <MDInput
                // className= "noteItemInput"
                type="text"
                name="description"
                multiline
                rows={3}
                placeholder={item?.notes ? item?.notes : intl.formatMessage({id:'inventory.edit.notes'})}
                // disabled={isDisabled}
                value={ itemForm.notes }
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputDescription(e, e.target.value, 'note')}
                  inputProps={{
                    maxLength: 100,
                  }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D2D6DA',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#7B809A',
                    opacity: 1,
                  },
                }}
                style={{ width: "100%", }}
              />
            </MDBox>

            <MDBox
              sx={{
                position: "absolute",
                bottom: 4,
                right: 8,
                fontSize: "0.8rem",
                color: "text.secondary", // prípadne uprav na konkrétnu farbu, napr. "#888"
              }}
            >
              {100 - (itemForm?.notes?.length || 0)}
            </MDBox>
          </MDBox>

          <MDBox 
            sx={{
                display: "flex", flexDirection: "row", justifyContent: "flex-end", 
                gap: "0.5rem", maxWidth: "100%", marginBottom: "0.5rem", marginTop: "1rem",
            }}
          >
            <MDButton 
              variant="outlined" 
              color="success" 
              // onClick={() => navigate(-1)}
              onClick={ closeWindow }
            >
              {intl.formatMessage({id:'button.cancel'})}
            </MDButton>
            <MDButton
              variant="contained"
              color="success"
              onClick={ handleSubmit }
            >
              {intl.formatMessage({id:'button.save'})}
            </MDButton>
          </MDBox>
  
        </MDBox>
      </Card>
    </Grid>
  )
};

export default injectIntl(EditItem);